import React from 'react';
import { Button } from 'antd';
import { Comment, CommentProps } from './Comment';
import { CommentReplyBar } from './CommentReplyBar';
import { ChevronRightIcon } from '../icons';
import './Comment.css';

export interface CommentListProps {
  /**
   * Array of comment data to display
   */
  comments: Omit<CommentProps, 'className'>[];
  /**
   * Maximum number of comments to display (default: 3)
   */
  maxComments?: number;
  /**
   * Callback when "View All" is clicked
   */
  onViewAll?: () => void;
  /**
   * Callback when the reply bar is clicked (opens add-comment flow)
   */
  onAddComment?: () => void;
  /**
   * Callback when a quick emoji is tapped on the reply bar
   */
  onEmojiClick?: (emoji: string) => void;
  /**
   * Title for the section (default: "Comments")
   */
  title?: string;
  /**
   * Whether to show the "View All" link (default: true if comments > maxComments)
   */
  showViewAll?: boolean;
  /**
   * Optional className for the container
   */
  className?: string;
  /**
   * Index of a comment to highlight with a blue border (auto-fades via CSS)
   */
  highlightedIndex?: number | null;
}

/**
 * CommentList - A list of comments with optional "View All" link
 * 
 * Displays up to `maxComments` (default 3) comments.
 * The "View All" link triggers `onViewAll` callback (typically opens CommentDrawer).
 */
export const CommentList: React.FC<CommentListProps> = ({
  comments,
  maxComments = 3,
  onViewAll,
  onAddComment,
  onEmojiClick,
  title = 'Comments',
  showViewAll,
  className,
  highlightedIndex,
}) => {
  const displayComments = comments.slice(0, maxComments);
  const hasMoreComments = comments.length > maxComments;
  const shouldShowViewAll = showViewAll ?? hasMoreComments;

  return (
    <div className={`comment-list-section${comments.length === 0 ? ' comment-list-section--empty' : ''} ${className || ''}`}>
      {comments.length === 0 ? (
        <p className="comment-list-empty-text">No chats yet. Be the first to chat!</p>
      ) : (
        <>
          <div className="comment-list">
            {displayComments.map((comment, index) => (
              <Comment
                key={index}
                {...comment}
                className={index === highlightedIndex ? 'spec-comment--highlighted' : undefined}
              />
            ))}
          </div>
          {shouldShowViewAll && onViewAll && (
            <div className="comment-list__view-all-row">
              <Button size="small" onClick={onViewAll}>
                See All
                <ChevronRightIcon size="m" tone="white" />
              </Button>
            </div>
          )}
        </>
      )}
      {onAddComment && (
        <CommentReplyBar onClick={onAddComment} onEmojiClick={onEmojiClick} />
      )}
    </div>
  );
};

export default CommentList;
